import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography,
} from '@mui/material';
import { PROBING_DEPTH_LEGEND, getArchLayout } from '../../constants/odontology';
import type { PeriodontalChart, PeriodontalMeasurements } from '../../types/odontology';
import {
  createPeriodontalChart,
  getPeriodontalChart,
  updatePeriodontalChart,
} from '../../services/odontologyService';
import { computePeriodontalSummary } from '../../utils/periodontalSummary';
import PeriodontalChartGrid from './PeriodontalChartGrid';
import { todayIso, useDentalProfessionals } from './odontologyHooks';

export type PeriodontalDialogMode = 'create' | 'edit' | 'view';

export interface PeriodontalChartDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  unitId?: string | null;
  chartId?: string | null;
  mode: PeriodontalDialogMode;
  defaultProfessionalId?: string | null;
  onSaved: () => void;
}

const EXAM_TYPES = [
  { value: 'initial', label: 'Exame inicial' },
  { value: 'reevaluation', label: 'Reavaliação' },
  { value: 'maintenance', label: 'Manutenção periodontal' },
];

const SummaryStat: React.FC<{ label: string; value: string; accent?: string }> = ({ label, value, accent }) => (
  <Box sx={{ minWidth: 92 }}>
    <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: accent || '#0F172A', letterSpacing: '-0.02em' }}>
      {value}
    </Typography>
  </Box>
);

const PeriodontalChartDialog: React.FC<PeriodontalChartDialogProps> = ({
  open,
  onClose,
  patientId,
  unitId,
  chartId,
  mode,
  defaultProfessionalId,
  onSaved,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const [examDate, setExamDate] = useState(todayIso());
  const [examType, setExamType] = useState('initial');
  const [professionalId, setProfessionalId] = useState('');
  const [status, setStatus] = useState<'draft' | 'finalized'>('draft');
  const [diagnosis, setDiagnosis] = useState('');
  const [notes, setNotes] = useState('');
  const [measurements, setMeasurements] = useState<PeriodontalMeasurements>({});

  const professionals = useDentalProfessionals(open);
  const readOnly = mode === 'view';
  const arch = getArchLayout('permanent');

  /**
   * Estado como estava ao abrir. Um periodontograma completo são 168 sondagens
   * digitadas a quatro mãos; fechar por engano e perder tudo é caro demais para
   * depender só do cuidado do usuário.
   */
  const baselineRef = useRef('');

  const formSnapshot = useMemo(
    () => JSON.stringify({ examDate, examType, professionalId, status, diagnosis, notes, measurements }),
    [examDate, examType, professionalId, status, diagnosis, notes, measurements],
  );

  const dirty = !readOnly && !loading && formSnapshot !== baselineRef.current;

  useEffect(() => {
    if (!open) return;
    setError(null);
    setConfirmDiscard(false);

    if (!chartId) {
      const initial = {
        examDate: todayIso(),
        examType: 'initial',
        professionalId: defaultProfessionalId || '',
        status: 'draft' as const,
        diagnosis: '',
        notes: '',
        measurements: {} as PeriodontalMeasurements,
      };
      setExamDate(initial.examDate);
      setExamType(initial.examType);
      setProfessionalId(initial.professionalId);
      setStatus(initial.status);
      setDiagnosis(initial.diagnosis);
      setNotes(initial.notes);
      setMeasurements(initial.measurements);
      baselineRef.current = JSON.stringify(initial);
      return;
    }

    let cancelled = false;
    setLoading(true);
    getPeriodontalChart(chartId)
      .then((chart: PeriodontalChart) => {
        if (cancelled) return;
        const loaded = {
          examDate: chart.exam_date?.slice(0, 10) || todayIso(),
          examType: chart.exam_type || 'initial',
          professionalId: chart.professional_id || '',
          status: chart.status || 'draft',
          diagnosis: chart.diagnosis || '',
          notes: chart.notes || '',
          measurements: chart.measurements || {},
        };
        setExamDate(loaded.examDate);
        setExamType(loaded.examType);
        setProfessionalId(loaded.professionalId);
        setStatus(loaded.status);
        setDiagnosis(loaded.diagnosis);
        setNotes(loaded.notes);
        setMeasurements(loaded.measurements);
        baselineRef.current = JSON.stringify(loaded);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.response?.data?.error || e?.message || 'Erro ao carregar periodontograma');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [open, chartId, defaultProfessionalId]);

  // Também protege contra fechar a aba / recarregar com sondagens não salvas.
  useEffect(() => {
    if (!open || !dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [open, dirty]);

  const requestClose = useCallback(() => {
    if (dirty) setConfirmDiscard(true);
    else onClose();
  }, [dirty, onClose]);

  const summary = useMemo(() => computePeriodontalSummary(measurements), [measurements]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        patient_id: patientId,
        unit_id: unitId || undefined,
        professional_id: professionalId || null,
        exam_date: examDate,
        exam_type: examType as PeriodontalChart['exam_type'],
        status,
        diagnosis: diagnosis.trim() || null,
        notes: notes.trim() || null,
        measurements,
      };

      if (chartId) await updatePeriodontalChart(chartId, payload);
      else await createPeriodontalChart(payload);

      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao salvar periodontograma');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // Com sondagens pendentes, clique fora e Esc pedem confirmação.
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') requestClose();
        else onClose();
      }}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#0F172A' }}>
            Periodontograma
          </Typography>
          {dirty && (
            <Typography
              component="span"
              sx={{
                fontSize: '0.7rem', fontWeight: 600, color: '#B45309',
                bgcolor: '#FFFBEB', border: '1px solid #FDE68A',
                borderRadius: 999, px: 1, py: 0.25,
              }}
            >
              Alterações não salvas
            </Typography>
          )}
        </Box>
        <Typography variant="body2" sx={{ color: '#64748B', mt: 0.25 }}>
          Sondagem de seis sítios por dente, mobilidade e lesão de furca
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Data do exame"
                  InputLabelProps={{ shrink: true }}
                  value={examDate}
                  disabled={readOnly}
                  onChange={(e) => setExamDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="perio-type-label">Tipo</InputLabel>
                  <Select
                    labelId="perio-type-label"
                    label="Tipo"
                    value={examType}
                    disabled={readOnly}
                    onChange={(e) => setExamType(e.target.value)}
                  >
                    {EXAM_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="perio-professional-label">Profissional</InputLabel>
                  <Select
                    labelId="perio-professional-label"
                    label="Profissional"
                    value={professionalId}
                    disabled={readOnly}
                    onChange={(e) => setProfessionalId(e.target.value)}
                  >
                    <MenuItem value="">Não informado</MenuItem>
                    {professionals.map((p) => <MenuItem key={p.id} value={p.id}>{p.full_name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="perio-status-label">Status</InputLabel>
                  <Select
                    labelId="perio-status-label"
                    label="Status"
                    value={status}
                    disabled={readOnly}
                    onChange={(e) => setStatus(e.target.value as 'draft' | 'finalized')}
                  >
                    <MenuItem value="draft">Rascunho</MenuItem>
                    <MenuItem value="finalized">Finalizado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box
              sx={{
                display: 'flex',
                gap: 3,
                flexWrap: 'wrap',
                p: 2,
                mb: 2.5,
                borderRadius: 2,
                border: '1px solid #E2E8F0',
                bgcolor: '#F8FAFC',
              }}
            >
              <SummaryStat label="Índice de placa" value={`${summary.plaque_index}%`} />
              <SummaryStat
                label="Sangramento"
                value={`${summary.bleeding_index}%`}
                accent={summary.bleeding_index >= 25 ? '#DC2626' : undefined}
              />
              <SummaryStat label="PS média" value={`${summary.mean_probing_depth} mm`} />
              <SummaryStat label="NIC médio" value={`${summary.mean_attachment_loss} mm`} />
              <SummaryStat label="Sítios 4–5 mm" value={String(summary.sites_pd_4_5)} accent="#B45309" />
              <SummaryStat label="Sítios ≥ 6 mm" value={String(summary.sites_pd_6_plus)} accent="#DC2626" />
              <SummaryStat label="Dentes examinados" value={String(summary.teeth_examined)} />
            </Box>

            {/* Legenda do mapa de calor da linha de sondagem. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Sondagem
              </Typography>
              {PROBING_DEPTH_LEGEND.map((tone) => (
                <Box key={tone.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 18, height: 18, borderRadius: 0.75,
                      bgcolor: tone.background, border: `1px solid ${tone.text}33`,
                    }}
                  />
                  <Typography sx={{ fontSize: '0.72rem', color: tone.text, fontWeight: 600 }}>
                    {tone.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            <PeriodontalChartGrid
              title="Arcada superior"
              teeth={[...arch.upper.right, ...arch.upper.left]}
              measurements={measurements}
              readOnly={readOnly}
              onChange={setMeasurements}
            />

            <PeriodontalChartGrid
              title="Arcada inferior"
              teeth={[...arch.lower.right, ...arch.lower.left]}
              measurements={measurements}
              readOnly={readOnly}
              onChange={setMeasurements}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  label="Diagnóstico periodontal"
                  value={diagnosis}
                  disabled={readOnly}
                  onChange={(e) => setDiagnosis(e.target.value)}
                  placeholder="Ex.: Periodontite estágio III, grau B, generalizada"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  label="Observações"
                  value={notes}
                  disabled={readOnly}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={requestClose} sx={{ textTransform: 'none', color: '#64748B' }}>
          {readOnly ? 'Fechar' : 'Cancelar'}
        </Button>
        {!readOnly && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || loading}
            sx={{ textTransform: 'none', bgcolor: '#0D9488', '&:hover': { bgcolor: '#0F766E' } }}
          >
            {saving ? 'Salvando…' : 'Salvar'}
          </Button>
        )}
      </DialogActions>

      <Dialog open={confirmDiscard} onClose={() => setConfirmDiscard(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', color: '#0F172A' }}>
          Descartar as alterações?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#475569' }}>
            As sondagens e observações digitadas neste exame ainda não foram salvas e serão perdidas.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmDiscard(false)} sx={{ textTransform: 'none', color: '#64748B' }}>
            Continuar editando
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => { setConfirmDiscard(false); onClose(); }}
            sx={{ textTransform: 'none' }}
          >
            Descartar
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default PeriodontalChartDialog;
